import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  ShieldCheck,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  UserCheck,
  Users,
  FileCheck,
  Ban,
  Timer,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

interface ApprovalData {
  summary: {
    pendingApprovals: number;
    approvedByPresident: number;
    approvedBySecretary: number;
    approvedByAccountant: number;
    rejected: number;
  };
  approvalTimeline: Array<{
    date: string;
    approvals: number;
    rejections: number;
  }>;
  leaderActions: Array<{
    date: string;
    leader: string;
    action: string;
    item: string;
    status: string;
  }>;
  loanApprovalBreakdown: Array<{
    stage: string;
    count: number;
  }>;
  pendingItems: Array<{
    id: string;
    type: string;
    description: string;
    submittedBy: string;
    submittedAt: string;
    requiredApprovers: number;
    currentApprovers: number;
  }>;
}

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    red: "bg-red-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    purple: "bg-purple-50",
    gray: "bg-gray-100",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    red: "text-red-600",
    blue: "text-blue-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const actionBadge: Record<string, string> = {
  approved: "bg-[#009A44]/10 text-[#009A44]",
  rejected: "bg-red-50 text-red-600",
  pending: "bg-amber-50 text-amber-600",
};

const stageColors: Record<string, string> = {
  "0/3": "bg-gray-100 text-gray-600",
  "1/3": "bg-amber-50 text-amber-600",
  "2/3": "bg-blue-50 text-blue-600",
  "3/3": "bg-[#009A44]/10 text-[#009A44]",
};

export default function ApprovalReport() {
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_REPORT_FILTERS);

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-approvals", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.dateStart) params.dateStart = filters.dateStart;
      if (filters.dateEnd) params.dateEnd = filters.dateEnd;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.leader) params.leader = filters.leader;

      const { data } = await api.get("/reports/approvals", { params });
      return data?.data as ApprovalData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || {
    pendingApprovals: 0,
    approvedByPresident: 0,
    approvedBySecretary: 0,
    approvedByAccountant: 0,
    rejected: 0,
  };
  const approvalTimeline = rawData?.approvalTimeline || [];
  const leaderActions = rawData?.leaderActions || [];
  const loanApprovalBreakdown = rawData?.loanApprovalBreakdown || [];
  const pendingItems = rawData?.pendingItems || [];

  const leaderActionColumns = [
    { key: "date", label: "Date" },
    { key: "leader", label: "Leader" },
    { key: "action", label: "Action" },
    { key: "item", label: "Item" },
    { key: "status", label: "Status" },
  ];

  const pendingItemsColumns = [
    { key: "id", label: "ID" },
    { key: "type", label: "Type" },
    { key: "description", label: "Description" },
    { key: "submittedBy", label: "Submitted By" },
    { key: "submittedAt", label: "Submitted At" },
    { key: "currentApprovers", label: "Approvals" },
    { key: "requiredApprovers", label: "Required" },
  ];

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Approval Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Leader approval tracking and pending items overview
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load approval report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <ShieldCheck className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Approval Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Leader approval tracking and pending items overview
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["dateStart", "dateEnd", "month", "year"]}
        />

        {/* Summary Cards - Row 1 */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Pending Approvals"
            value={summary.pendingApprovals}
            icon={<Clock className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
          <SummaryCard
            title="Approved by President"
            value={summary.approvedByPresident}
            icon={<UserCheck className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Approved by Secretary"
            value={summary.approvedBySecretary}
            icon={<Users className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Approved by Accountant"
            value={summary.approvedByAccountant}
            icon={<FileCheck className="h-5 w-5" />}
            accentColor="purple"
            loading={loading}
          />
        </div>

        {/* Rejected Summary */}
        <div className="grid gap-4 sm:grid-cols-1">
          <SummaryCard
            title="Rejected"
            value={summary.rejected}
            icon={<Ban className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
        </div>

        {/* Approval Timeline Chart */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                <TrendingUp className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              <h3 className="text-sm font-semibold">Approval Timeline</h3>
            </div>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : approvalTimeline.length === 0 ? (
              <div className="py-12 text-center">
                <TrendingUp className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No timeline data available</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={approvalTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="approvals"
                      name="Approvals"
                      stroke="#009A44"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="rejections"
                      name="Rejections"
                      stroke="#F44336"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Leader Action History */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                    <UserCheck className="h-3.5 w-3.5 text-[#009A44]" />
                  </div>
                  <h3 className="text-sm font-semibold">Leader Action History</h3>
                </div>
                <ReportExport
                  data={leaderActions}
                  filename="approval-leader-actions"
                  title="Leader Action History"
                  columns={leaderActionColumns}
                />
              </div>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                      <Skeleton className="h-4 w-20" />
                    </div>
                  ))}
                </div>
              ) : leaderActions.length === 0 ? (
                <div className="py-12 text-center">
                  <UserCheck className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                  <p className="text-sm text-gray-500">No leader actions recorded</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b text-left">
                        <th className="pb-2 pr-4 font-medium text-gray-500">Date</th>
                        <th className="pb-2 pr-4 font-medium text-gray-500">Leader</th>
                        <th className="pb-2 pr-4 font-medium text-gray-500">Action</th>
                        <th className="pb-2 pr-4 font-medium text-gray-500">Item</th>
                        <th className="pb-2 font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderActions.map((action, index) => (
                        <tr
                          key={index}
                          className="border-b last:border-b-0 hover:bg-gray-50/50"
                        >
                          <td className="py-3 pr-4 text-gray-600">
                            {action.date ? new Date(action.date).toLocaleDateString() : "-"}
                          </td>
                          <td className="py-3 pr-4 font-medium text-gray-900">
                            {action.leader}
                          </td>
                          <td className="py-3 pr-4 text-gray-600">{action.action}</td>
                          <td className="py-3 pr-4 text-gray-600">{action.item}</td>
                          <td className="py-3">
                            <span
                              className={cn(
                                "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                                actionBadge[action.status] || "bg-gray-100 text-gray-600"
                              )}
                            >
                              {action.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loan Approval Breakdown */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <BarChart3 className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Loan Approval Breakdown</h3>
              </div>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : loanApprovalBreakdown.length === 0 ? (
                <div className="py-12 text-center">
                  <BarChart3 className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                  <p className="text-sm text-gray-500">No approval breakdown data</p>
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={loanApprovalBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Bar dataKey="count" name="Loans" fill="#009A44" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Stage Legend */}
              {!loading && loanApprovalBreakdown.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {loanApprovalBreakdown.map((item) => (
                    <div
                      key={item.stage}
                      className="flex items-center justify-between rounded-lg border border-gray-100 p-2"
                    >
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                          stageColors[item.stage] || "bg-gray-100 text-gray-600"
                        )}
                      >
                        {item.stage}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending Items Needing Attention */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-50">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                </div>
                <h3 className="text-sm font-semibold">Pending Items Needing Attention</h3>
                {!loading && pendingItems.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
                    {pendingItems.length}
                  </span>
                )}
              </div>
              <ReportExport
                data={pendingItems}
                filename="approval-pending-items"
                title="Pending Approval Items"
                columns={pendingItemsColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : pendingItems.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[#009A44]/30" />
                <p className="text-sm text-gray-500">All caught up! No pending items.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">ID</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Type</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Description</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Submitted By</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Submitted</th>
                      <th className="pb-2 font-medium text-gray-500 text-center">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingItems.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b last:border-b-0 hover:bg-gray-50/50"
                      >
                        <td className="py-3 pr-4 font-mono text-xs text-gray-500">
                          {item.id}
                        </td>
                        <td className="py-3 pr-4">
                          <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600 capitalize">
                            {item.type}
                          </span>
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900 max-w-[200px] truncate">
                          {item.description}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {item.submittedBy}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : "-"}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-2 w-16 rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className="h-full bg-[#009A44] rounded-full transition-all"
                                style={{
                                  width: `${(item.currentApprovers / item.requiredApprovers) * 100}%`,
                                }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-600">
                              {item.currentApprovers}/{item.requiredApprovers}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
